import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { TaskFormDialog } from "@/components/TaskFormDialog";
import { api } from "@/lib/api";

const Ctx = createContext(null);
const LAST_SYNC_KEY = "shb_last_sync";
const STALE_MS = 5 * 60 * 1000; // uygulama açılışında / geri dönüşte bu süre geçtiyse eşitle

export const QuickCreateProvider = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [syncError, setSyncError] = useState(null);
  const [lastSync, setLastSync] = useState(() => localStorage.getItem(LAST_SYNC_KEY));
  const running = useRef(false);

  const sync = useCallback(async ({ silent = false } = {}) => {
    if (running.current) return;
    running.current = true;
    setSyncing(true);
    try {
      const { data } = await api.post("/sheet/pull");
      const now = new Date().toISOString();
      localStorage.setItem(LAST_SYNC_KEY, now);
      setLastSync(now);
      setSyncError(null);
      setRefreshKey((k) => k + 1);
      if (!silent) toast.success(`Google Sheet ile eşitlendi (${data.imported} kayıt güncellendi).`);
    } catch (e) {
      // Eşitleme başarısız olsa da mevcut kayıtlar silinmez, yalnızca hata gösterilir
      const detail = e.response?.data?.detail || "Google Sheet'e ulaşılamadı.";
      setSyncError(detail);
      if (!silent) toast.error(detail, { duration: 8000 });
    } finally {
      setSyncing(false);
      running.current = false;
    }
  }, []);

  const syncIfStale = useCallback(() => {
    const last = localStorage.getItem(LAST_SYNC_KEY);
    if (!last || Date.now() - new Date(last).getTime() > STALE_MS) sync({ silent: true });
  }, [sync]);

  // Uygulama açılışında bir kez
  useEffect(() => {
    syncIfStale();
  }, [syncIfStale]);

  // Kullanıcı sekmeye geri döndüğünde (arka plan zamanlayıcısı yok)
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") syncIfStale();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [syncIfStale]);

  return (
    <Ctx.Provider value={{ open, setOpen, refreshKey, sync, syncing, syncError, lastSync }}>
      {children}
      <TaskFormDialog
        open={open}
        onOpenChange={setOpen}
        initial={null}
        onSaved={() => setRefreshKey((k) => k + 1)}
      />
    </Ctx.Provider>
  );
};

export const useQuickCreate = () => useContext(Ctx);
